import { getSupabaseClient } from "./supabase";
import { runtimeEnv } from "./runtimeEnv";

export type RuntimeConfig = {
  environmentLabel: string;
  betaGateEnabled: boolean;
  leaderboardEnabled: boolean;
  telemetryEnabled: boolean;
  productAnalyticsEnabled: boolean;
  sessionReplayEnabled: boolean;
  errorMonitoringEnabled: boolean;
  feedbackSurveyEnabled: boolean;
  feedbackUrl: string;
  discordUrl: string;
  supportEmail: string;
  gateTitle: string;
  gateBody: string;
};

type RuntimeConfigRow = Partial<{
  environment_label: unknown;
  beta_gate_enabled: unknown;
  leaderboard_enabled: unknown;
  telemetry_enabled: unknown;
  product_analytics_enabled: unknown;
  session_replay_enabled: unknown;
  error_monitoring_enabled: unknown;
  feedback_survey_enabled: unknown;
  feedback_url: unknown;
  discord_url: unknown;
  support_email: unknown;
  gate_title: unknown;
  gate_body: unknown;
}>;

function parseBooleanEnv(value: string | undefined, fallback: boolean): boolean {
  if (value === undefined) {
    return fallback;
  }

  const normalizedValue = value.trim().toLowerCase();
  if (normalizedValue === "true" || normalizedValue === "1" || normalizedValue === "yes") {
    return true;
  }

  if (normalizedValue === "false" || normalizedValue === "0" || normalizedValue === "no") {
    return false;
  }

  return fallback;
}

function sanitizeOptionalUrl(value: unknown, fallback = ""): string {
  if (typeof value !== "string") {
    return fallback;
  }

  const normalizedValue = value.trim();
  if (normalizedValue.length === 0) {
    return fallback;
  }

  if (
    normalizedValue.startsWith("https://") ||
    normalizedValue.startsWith("http://") ||
    normalizedValue.startsWith("mailto:")
  ) {
    return normalizedValue;
  }

  return fallback;
}

function sanitizeText(value: unknown, fallback: string): string {
  if (typeof value !== "string") {
    return fallback;
  }

  const normalizedValue = value.trim();
  return normalizedValue.length > 0 ? normalizedValue : fallback;
}

function sanitizeBoolean(value: unknown, fallback: boolean): boolean {
  if (typeof value === "boolean") {
    return value;
  }

  if (typeof value === "string") {
    return parseBooleanEnv(value, fallback);
  }

  return fallback;
}

export function getDefaultRuntimeConfig(): RuntimeConfig {
  const leaderboardEnabledFallback = runtimeEnv.VITE_SUPABASE_URL !== undefined;
  const productAnalyticsEnabledFallback = runtimeEnv.VITE_POSTHOG_KEY !== undefined;
  const sessionReplayEnabledFallback = runtimeEnv.VITE_POSTHOG_KEY !== undefined;
  const errorMonitoringEnabledFallback = runtimeEnv.VITE_SENTRY_DSN !== undefined;

  return {
    environmentLabel:
      runtimeEnv.VITE_BETA_ENVIRONMENT_LABEL?.trim() ||
      (runtimeEnv.DEV === true ? "Local Build" : "Public Build"),
    betaGateEnabled: parseBooleanEnv(runtimeEnv.VITE_BETA_GATE_ENABLED, false),
    leaderboardEnabled: parseBooleanEnv(
      runtimeEnv.VITE_BETA_LEADERBOARD_ENABLED,
      leaderboardEnabledFallback
    ),
    telemetryEnabled: parseBooleanEnv(
      runtimeEnv.VITE_BETA_TELEMETRY_ENABLED,
      leaderboardEnabledFallback
    ),
    productAnalyticsEnabled: parseBooleanEnv(
      runtimeEnv.VITE_BETA_PRODUCT_ANALYTICS_ENABLED,
      productAnalyticsEnabledFallback
    ),
    sessionReplayEnabled: parseBooleanEnv(
      runtimeEnv.VITE_BETA_SESSION_REPLAY_ENABLED,
      sessionReplayEnabledFallback
    ),
    errorMonitoringEnabled: parseBooleanEnv(
      runtimeEnv.VITE_BETA_ERROR_MONITORING_ENABLED,
      errorMonitoringEnabledFallback
    ),
    feedbackSurveyEnabled: parseBooleanEnv(
      runtimeEnv.VITE_BETA_FEEDBACK_SURVEY_ENABLED,
      false
    ),
    feedbackUrl: sanitizeOptionalUrl(runtimeEnv.VITE_BETA_FEEDBACK_URL),
    discordUrl: sanitizeOptionalUrl(runtimeEnv.VITE_BETA_DISCORD_URL),
    supportEmail: sanitizeOptionalUrl(runtimeEnv.VITE_BETA_SUPPORT_EMAIL, ""),
    gateTitle: sanitizeText(runtimeEnv.VITE_BETA_GATE_TITLE, "Tavela Tiles"),
    gateBody: sanitizeText(
      runtimeEnv.VITE_BETA_GATE_BODY,
      "Start a run and climb the public leaderboard."
    )
  };
}

export function normalizeRuntimeConfig(
  value: unknown,
  fallback: RuntimeConfig = getDefaultRuntimeConfig()
): RuntimeConfig {
  if (typeof value !== "object" || value === null) {
    return fallback;
  }

  const record = value as RuntimeConfigRow;

  return {
    environmentLabel: sanitizeText(record.environment_label, fallback.environmentLabel),
    betaGateEnabled: sanitizeBoolean(record.beta_gate_enabled, fallback.betaGateEnabled),
    leaderboardEnabled: sanitizeBoolean(record.leaderboard_enabled, fallback.leaderboardEnabled),
    telemetryEnabled: sanitizeBoolean(record.telemetry_enabled, fallback.telemetryEnabled),
    productAnalyticsEnabled: sanitizeBoolean(
      record.product_analytics_enabled,
      fallback.productAnalyticsEnabled
    ),
    sessionReplayEnabled: sanitizeBoolean(
      record.session_replay_enabled,
      fallback.sessionReplayEnabled
    ),
    errorMonitoringEnabled: sanitizeBoolean(
      record.error_monitoring_enabled,
      fallback.errorMonitoringEnabled
    ),
    feedbackSurveyEnabled: sanitizeBoolean(
      record.feedback_survey_enabled,
      fallback.feedbackSurveyEnabled
    ),
    feedbackUrl: sanitizeOptionalUrl(record.feedback_url, fallback.feedbackUrl),
    discordUrl: sanitizeOptionalUrl(record.discord_url, fallback.discordUrl),
    supportEmail: sanitizeOptionalUrl(record.support_email, fallback.supportEmail),
    gateTitle: sanitizeText(record.gate_title, fallback.gateTitle),
    gateBody: sanitizeText(record.gate_body, fallback.gateBody)
  };
}

function applyEnvironmentOverrides(config: RuntimeConfig): RuntimeConfig {
  return {
    environmentLabel:
      runtimeEnv.VITE_BETA_ENVIRONMENT_LABEL !== undefined
        ? sanitizeText(runtimeEnv.VITE_BETA_ENVIRONMENT_LABEL, config.environmentLabel)
        : config.environmentLabel,
    betaGateEnabled:
      runtimeEnv.VITE_BETA_GATE_ENABLED !== undefined
        ? parseBooleanEnv(runtimeEnv.VITE_BETA_GATE_ENABLED, config.betaGateEnabled)
        : config.betaGateEnabled,
    leaderboardEnabled:
      runtimeEnv.VITE_BETA_LEADERBOARD_ENABLED !== undefined
        ? parseBooleanEnv(runtimeEnv.VITE_BETA_LEADERBOARD_ENABLED, config.leaderboardEnabled)
        : config.leaderboardEnabled,
    telemetryEnabled:
      runtimeEnv.VITE_BETA_TELEMETRY_ENABLED !== undefined
        ? parseBooleanEnv(runtimeEnv.VITE_BETA_TELEMETRY_ENABLED, config.telemetryEnabled)
        : config.telemetryEnabled,
    productAnalyticsEnabled:
      runtimeEnv.VITE_BETA_PRODUCT_ANALYTICS_ENABLED !== undefined
        ? parseBooleanEnv(
            runtimeEnv.VITE_BETA_PRODUCT_ANALYTICS_ENABLED,
            config.productAnalyticsEnabled
          )
        : config.productAnalyticsEnabled,
    sessionReplayEnabled:
      runtimeEnv.VITE_BETA_SESSION_REPLAY_ENABLED !== undefined
        ? parseBooleanEnv(
            runtimeEnv.VITE_BETA_SESSION_REPLAY_ENABLED,
            config.sessionReplayEnabled
          )
        : config.sessionReplayEnabled,
    errorMonitoringEnabled:
      runtimeEnv.VITE_BETA_ERROR_MONITORING_ENABLED !== undefined
        ? parseBooleanEnv(
            runtimeEnv.VITE_BETA_ERROR_MONITORING_ENABLED,
            config.errorMonitoringEnabled
          )
        : config.errorMonitoringEnabled,
    feedbackSurveyEnabled:
      runtimeEnv.VITE_BETA_FEEDBACK_SURVEY_ENABLED !== undefined
        ? parseBooleanEnv(
            runtimeEnv.VITE_BETA_FEEDBACK_SURVEY_ENABLED,
            config.feedbackSurveyEnabled
          )
        : config.feedbackSurveyEnabled,
    feedbackUrl:
      runtimeEnv.VITE_BETA_FEEDBACK_URL !== undefined
        ? sanitizeOptionalUrl(runtimeEnv.VITE_BETA_FEEDBACK_URL, config.feedbackUrl)
        : config.feedbackUrl,
    discordUrl:
      runtimeEnv.VITE_BETA_DISCORD_URL !== undefined
        ? sanitizeOptionalUrl(runtimeEnv.VITE_BETA_DISCORD_URL, config.discordUrl)
        : config.discordUrl,
    supportEmail:
      runtimeEnv.VITE_BETA_SUPPORT_EMAIL !== undefined
        ? sanitizeOptionalUrl(runtimeEnv.VITE_BETA_SUPPORT_EMAIL, config.supportEmail)
        : config.supportEmail,
    gateTitle:
      runtimeEnv.VITE_BETA_GATE_TITLE !== undefined
        ? sanitizeText(runtimeEnv.VITE_BETA_GATE_TITLE, config.gateTitle)
        : config.gateTitle,
    gateBody:
      runtimeEnv.VITE_BETA_GATE_BODY !== undefined
        ? sanitizeText(runtimeEnv.VITE_BETA_GATE_BODY, config.gateBody)
        : config.gateBody
  };
}

export async function loadRuntimeConfig(): Promise<RuntimeConfig> {
  const fallback = getDefaultRuntimeConfig();
  const client = await getSupabaseClient();

  if (client === null) {
    return fallback;
  }

  const { data, error } = await client
    .from("beta_runtime_config")
    .select(
      "environment_label,beta_gate_enabled,leaderboard_enabled,telemetry_enabled,product_analytics_enabled,session_replay_enabled,error_monitoring_enabled,feedback_survey_enabled,feedback_url,discord_url,support_email,gate_title,gate_body"
    )
    .eq("id", "default")
    .maybeSingle();

  if (error || data === null) {
    return fallback;
  }

  return applyEnvironmentOverrides(normalizeRuntimeConfig(data, fallback));
}
